import React, { useState, useEffect, useRef } from 'react'; // Import useRef
import { motion, useScroll, useTransform } from 'framer-motion'; // Import motion and hooks
import PageLayout from '../../components/layout/PageLayout';
import Section from '../../components/layout/Section';
import CTASection from '../../components/common/CTASection'; // Ensure this is the modified one
import ProjectHero from './components/ProjectHero';
import ProjectFilters from './components/ProjectFilters';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import ProjectStats from './components/ProjectStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const FeaturedProjects = () => {
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    industry: [],
    projectType: [],
    outcome: []
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredProjects, setFilteredProjects] = useState([]);

  // Government Projects Portfolio
  const projects = [
    {
      id: 1,
      title: "MP PHED – Jal Jeevan Mission, Khandwa",
      description: "Comprehensive water infrastructure project under the Jal Jeevan Mission for Madhya Pradesh Public Health Engineering Department, ensuring clean water access to rural communities in Khandwa district.",
      industry: "Government Infrastructure",
      projectType: "Water Infrastructure",
      outcome: "Public Service Enhancement",
      client: "MP Public Health Engineering Department",
      clientRole: "Project Director",
      duration: "24 months",
      image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=800&h=600&fit=crop",
      heroImage: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?w=1200&h=800&fit=crop",
      keyMetrics: [
        { value: "100%", label: "Rural Coverage" },
        { value: "50,000+", label: "Beneficiaries" },
        { value: "98%", label: "Quality Standards" },
        { value: "24/7", label: "Water Supply" }
      ],
      technologies: ["Water Treatment Systems", "Pipeline Networks", "Quality Monitoring", "Distribution Systems", "Pumping Stations", "Control Systems"],
      tags: ["Jal Jeevan Mission", "Rural Water Supply", "Government Project"],
      challenge: `The Khandwa district faced significant challenges in providing clean, reliable water supply to rural communities. Existing infrastructure was inadequate, and water quality issues were affecting public health. The project required comprehensive planning to ensure sustainable water access.`,
      partnership: `ONS METTLE collaborated closely with MP PHED officials, local authorities, and community representatives to understand specific regional needs. Our team worked within government guidelines and timelines while ensuring minimal disruption to existing services.`,
      innovation: `We implemented advanced water treatment systems, efficient distribution networks, and real-time quality monitoring solutions. The project included innovative pipeline design, automated pumping stations, and sustainable maintenance protocols.`,
      impact: `The project successfully provided clean water access to over 50,000 rural beneficiaries, achieved 100% coverage in target areas, and established 24/7 water supply with 98% quality standards compliance. The infrastructure is designed for long-term sustainability.`,
      teamMembers: [
        { name: "Rajesh Kumar", role: "Project Head" },
        { name: "Priya Sharma", role: "Civil Engineer" },
        { name: "Amit Patel", role: "Water Systems Specialist" },
        { name: "Sunita Verma", role: "Quality Assurance Manager" }
      ],
      testimonial: {
        quote: "ONS METTLE delivered exceptional results for the Jal Jeevan Mission. Their technical expertise and commitment to quality ensured successful project completion within timeline and budget.",
        author: "Dr. Ramesh Gupta",
        role: "Project Director, MP PHED"
      }
    },
    {
      id: 2,
      title: "BPCL – Gujarat, Madhya Pradesh, Maharashtra & Goa",
      description: "Multi-state telecommunications infrastructure project for Bharat Petroleum Corporation Limited, implementing advanced communication systems across fuel stations and facilities in four states.",
      industry: "Energy & Petroleum",
      projectType: "Telecommunications Infrastructure",
      outcome: "Operational Excellence",
      client: "Bharat Petroleum Corporation Limited",
      clientRole: "General Manager - IT",
      duration: "18 months",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=800&h=600&fit=crop",
      heroImage: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=1200&h=800&fit=crop",
      keyMetrics: [
        { value: "4", label: "States Covered" },
        { value: "500+", label: "Locations Connected" },
        { value: "99.5%", label: "Network Uptime" },
        { value: "100%", label: "Compliance Achievement" }
      ],
      technologies: ["Fiber Optic Networks", "Wireless Communication", "Network Security", "Monitoring Systems", "Data Centers", "Backup Systems"],
      tags: ["Multi-State Project", "Petroleum Industry", "Network Infrastructure"],
      challenge: `BPCL required a robust telecommunications infrastructure across multiple states to ensure seamless communication between fuel stations, regional offices, and headquarters. The challenge involved coordinating across different state regulations and ensuring uninterrupted operations.`,
      partnership: `ONS METTLE worked closely with BPCL's IT department and regional teams across Gujarat, Madhya Pradesh, Maharashtra, and Goa. Our team coordinated with local authorities and ensured compliance with state-specific regulations while maintaining project timelines.`,
      innovation: `We implemented a scalable telecommunications network with redundant connectivity, advanced security protocols, and centralized monitoring systems. The solution included fiber optic backbone, wireless last-mile connectivity, and disaster recovery mechanisms.`,
      impact: `The project successfully connected 500+ locations across four states, achieved 99.5% network uptime, and enabled real-time communication and data exchange. The infrastructure supports BPCL's operational efficiency and future expansion plans.`,
      teamMembers: [
        { name: "Saurabh Joshi", role: "Project Head" },
        { name: "Rahul Kumar", role: "Technical Manager" },
        { name: "Deepak Singh", role: "Network Architect" },
        { name: "Kavita Sharma", role: "Implementation Lead" }
      ],
      testimonial: {
        quote: "ONS METTLE's expertise in multi-state telecommunications deployment was exceptional. They delivered a robust network infrastructure that supports our operations across all four states seamlessly.",
        author: "Suresh Nair",
        role: "General Manager - IT, BPCL"
      }
    },
    {
      id: 3,
      title: "AIIMS – Deoghar",
      description: "Advanced telecommunications and infrastructure project for All India Institute of Medical Sciences, Deoghar, implementing state-of-the-art communication systems and supporting infrastructure for the premier medical institution.",
      industry: "Healthcare & Medical Education",
      projectType: "Medical Infrastructure",
      outcome: "Healthcare Excellence",
      client: "All India Institute of Medical Sciences, Deoghar",
      clientRole: "Director",
      duration: "20 months",
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      heroImage: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=1200&h=800&fit=crop",
      keyMetrics: [
        { value: "100%", label: "Infrastructure Completion" },
        { value: "24/7", label: "Communication Uptime" },
        { value: "1000+", label: "Bed Capacity Support" },
        { value: "99.9%", label: "System Reliability" }
      ],
      technologies: ["Medical Communication Systems", "Hospital Networks", "Emergency Communication", "Data Centers", "Backup Systems", "Security Infrastructure"],
      tags: ["AIIMS Project", "Medical Infrastructure", "Government Healthcare"],
      challenge: `AIIMS Deoghar required a comprehensive telecommunications infrastructure to support a world-class medical institution. The project demanded high reliability, security, and integration with medical equipment and systems while ensuring uninterrupted communication for critical healthcare operations.`,
      partnership: `ONS METTLE collaborated with AIIMS administration, medical professionals, and technical teams to understand the unique requirements of a premier medical institution. Our team worked within strict healthcare regulations and quality standards.`,
      innovation: `We implemented a robust telecommunications infrastructure with redundant systems, emergency communication protocols, and integration capabilities for medical equipment. The solution includes high-speed data networks, voice communication systems, and specialized medical communication channels.`,
      impact: `The project successfully established a state-of-the-art communication infrastructure supporting 1000+ bed capacity, achieved 99.9% system reliability, and enabled 24/7 communication uptime. The infrastructure supports AIIMS Deoghar's mission of providing world-class healthcare and medical education.`,
      teamMembers: [
        { name: "Dr. Vikram Singh", role: "Medical Systems Consultant" },
        { name: "Anita Kumari", role: "Infrastructure Lead" },
        { name: "Manoj Tiwari", role: "Network Specialist" },
        { name: "Ritu Agarwal", role: "Quality Manager" }
      ],
      testimonial: {
        quote: "ONS METTLE delivered exceptional telecommunications infrastructure that meets the highest standards required for a premier medical institution. Their understanding of healthcare requirements was outstanding.",
        author: "Prof. Dr. Ashok Kumar",
        role: "Director, AIIMS Deoghar"
      }
    },
  ];

  const featuredProject = projects[0];
  const projectStats = {
    totalProjects: "3",
    clientsSatisfied: "100%",
    industriesServed: "12+",
    averageROI: "250%"
  };

  // Filter projects based on active filters and search query
  useEffect(() => {
    let filtered = projects;

    // Apply filters
    Object.keys(activeFilters).forEach(category => {
      if (activeFilters[category].length > 0) {
        filtered = filtered.filter(project => {
          const projectValue = project[category];
          return activeFilters[category].some(filter =>
            projectValue.toLowerCase().includes(filter.replace('-', ' '))
          );
        });
      }
    });

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(project =>
        project.title.toLowerCase().includes(query) ||
        project.description.toLowerCase().includes(query) ||
        project.technologies.some(tech => tech.toLowerCase().includes(query)) ||
        project.industry.toLowerCase().includes(query) ||
        project.client.toLowerCase().includes(query)
      );
    }

    setFilteredProjects(filtered);
  }, [activeFilters, searchQuery]);

  const handleFilterChange = (category, filters) => {
    setActiveFilters(prev => ({
      ...prev,
      [category]: filters
    }));
  };

  const handleClearFilters = () => {
    setActiveFilters({
      industry: [],
      projectType: [],
      outcome: []
    });
    setSearchQuery('');
  };

  const handleViewDetails = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  const handleShare = (project) => {
    if (navigator.share) {
      navigator.share({
        title: project.title,
        text: project.description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // *** FRAMER MOTION FOR CTA SECTION BACKGROUND ***
  const ctaSectionRef = useRef(null); // Create a ref for the CTA section
  const { scrollYProgress: ctaScrollYProgress } = useScroll({
    target: ctaSectionRef, // Target the CTA section
    offset: ['start end', 'end start'] // When target starts entering view to when it leaves view
  });

  // Define how the gradient colors change with scroll progress over the CTA section
  const ctaColor1 = useTransform(
    ctaScrollYProgress,
    [0, 0.5, 1], // Input range: when 0% of CTA visible, 50% visible, 100% visible
    ['#3182CE', '#2B6CB0', '#2C5282'] // From a lighter blue to a darker blue
    // Corresponds to Tailwind: from-blue-600, via-blue-700, to-blue-800
  );

  const ctaColor2 = useTransform(
    ctaScrollYProgress,
    [0, 0.5, 1],
    ['#4299E1', '#3182CE', '#2B6CB0'] // From a slightly lighter blue to a darker blue
    // Corresponds to Tailwind: from-blue-500, via-blue-600, to-blue-700
  );

  const ctaColor3 = useTransform(
    ctaScrollYProgress,
    [0, 0.5, 1],
    ['#63B3ED', '#4299E1', '#3182CE'] // From an even lighter blue to a darker blue
    // Corresponds to Tailwind: from-blue-400, via-blue-500, to-blue-600
  );

  // Global background gradient animation for the page
  const { scrollYProgress } = useScroll(); // Get scroll progress (0 to 1)

  const globalGradientColor1 = useTransform(
    scrollYProgress,
    [0, 1], // Input scroll range (0% to 100% of scrollable content)
    ['#edf2f7', '#e2e8f0'] // Output color range for the first color (light blue-gray to slightly darker)
  );

  const globalGradientColor2 = useTransform(
    scrollYProgress,
    [0, 1], // Input scroll range
    ['#e2e8f0', '#cbd5e1'] // Output color range for the second color (light blue-gray to a bit darker)
  );

  const globalGradientColor3 = useTransform(
    scrollYProgress,
    [0, 1], // Input scroll range
    ['#cbd5e1', '#a0aec0'] // Output color range for the third color (light blue-gray to a darker grey)
  );


  return (
    <PageLayout
      title="Featured Projects - Ons Mettle Corporate"
      description="Explore our portfolio of transformative projects across industries. See how Ons Mettle delivers measurable results through innovative solutions and strategic partnerships."
      keywords={['case studies', 'project portfolio', 'digital transformation', 'business results', 'client success stories']}
    >
      <motion.div
        style={{
          background: `linear-gradient(to bottom right, ${globalGradientColor1}, ${globalGradientColor2}, ${globalGradientColor3})`
        }}
        className="min-h-screen" // Ensure it takes full height to show scroll effect
      >
        {/* Hero Section */}
        <Section padding="py-12 lg:py-20">
          <ProjectHero
            project={featuredProject}
            onViewDetails={handleViewDetails}
          />
        </Section>

        {/* Project Stats */}
        <Section padding="py-12">
          <ProjectStats stats={projectStats} />
        </Section>

        {/* Projects Gallery */}
        <Section padding="py-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 font-heading mb-4">
              Project Database
            </h2>
            <p className="text-lg text-slate-700 max-w-3xl mx-auto">
              Explore our comprehensive portfolio of transformation stories. Filter by industry,
              project type, or outcome focus to find relevant case studies.
            </p>
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Filters Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <ProjectFilters
                  activeFilters={activeFilters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={handleClearFilters}
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                  projectCount={filteredProjects.length}
                />
              </div>
            </div>

            {/* Projects Grid */}
            <div className="lg:col-span-3">
              {filteredProjects.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-8">
                  {filteredProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      onViewDetails={handleViewDetails}
                      onShare={handleShare}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="Search" size={48} className="text-slate-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-800 mb-2">
                    No projects found
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Try adjusting your filters or search terms to find relevant projects.
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleClearFilters}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Section>

        {/* Call to Action - Now animated */}
        <CTASection
          ref={ctaSectionRef} // Attach the ref here
          title="Ready to Start Your Transformation?"
          description="Every great project starts with a conversation. Let's discuss how we can help you achieve similar results in your industry."
          primaryCTA={{
            text: "Discuss Your Project",
            iconName: "MessageCircle",
            iconPosition: "left",
            onClick: () => console.log("Discuss project clicked")
          }}
          secondaryCTA={{
            text: "Download Portfolio",
            iconName: "Download",
            iconPosition: "left",
            onClick: () => console.log("Download portfolio clicked")
          }}
          // We're dynamically setting the background style here
          // Remove the static backgroundClass="bg-gradient-to-r from-primary via-secondary to-accent"
          // because we're overriding it with inline style.
          style={{
            background: `linear-gradient(to right, ${ctaColor1}, ${ctaColor2}, ${ctaColor3})`
          }}
        />

        {/* Project Modal */}
        <ProjectModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      </motion.div>
    </PageLayout>
  );
};

export default FeaturedProjects;